import React, { useEffect, useMemo, useRef, useState } from 'react';

// AOC-style date picker: a calendar button that opens a dark month calendar where only the
// given `dates` (the ones that have stored data) are selectable. Values use the API's
// "DD-MMM-YYYY" format (e.g. "29-Sep-2026").

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const C = {
  trigger: '#1f2329',
  triggerBorder: '#3a3f47',
  panel: '#1e1f22',
  text: '#e8e8e8',
  dim: '#6f7277',
  hover: '#2e3137',
  selected: '#a8c7fa',
  selectedText: '#0b1d3a',
  todayRing: '#8a8d93',
};

const parseDate = (s) => {
  const m = String(s || '').match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  if (!m) return null;
  const month = MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase());
  return month < 0 ? null : new Date(Number(m[3]), month, Number(m[1]));
};
const toValue = (d) => `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
const toLabel = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const monthKey = (y, m) => `${y}-${m}`;

const CalendarIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <line x1="3.5" y1="10" x2="20.5" y2="10" />
    <line x1="8" y1="3" x2="8" y2="7" />
    <line x1="16" y1="3" x2="16" y2="7" />
    <rect x="7" y="13" width="3" height="2.5" fill="currentColor" stroke="none" />
    <rect x="11" y="13" width="3" height="2.5" fill="currentColor" stroke="none" />
  </svg>
);

const Chevron = ({ dir }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points={dir === 'left' ? '15 5 8 12 15 19' : '9 5 16 12 9 19'} />
  </svg>
);

const CalendarPicker = ({
  value, dates = [], onChange, disabled = false,
  placeholder = 'Select Date', dialogLabel = 'Choose date', emptyText = 'No dates available',
  size = 'md', // 'sm': compact trigger (38px tall, 15px text, 16px icon)
  align = 'left', // 'right': the popup opens under the button's right edge (for a button at the page's right)
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('days'); // 'days' | 'months'
  const [view, setView] = useState(() => new Date());
  const [hovered, setHovered] = useState(null);
  const rootRef = useRef(null);

  const selected = parseDate(value);
  const available = useMemo(() => {
    const parsed = dates.map(parseDate).filter(Boolean);
    return { days: new Set(parsed.map(dayKey)), months: new Set(parsed.map((d) => monthKey(d.getFullYear(), d.getMonth()))), list: parsed };
  }, [dates]);

  // Open on the selected date's month, else the latest available date, else today.
  const openPicker = () => {
    if (disabled) return;
    const latest = [...available.list].sort((a, b) => b - a)[0];
    const anchor = selected || latest || new Date();
    setView(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    setMode('days');
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const today = new Date();
  const todayKey = dayKey(today);

  const cells = useMemo(() => {
    const lead = new Date(year, month, 1).getDay();
    const count = new Date(year, month + 1, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => new Date(year, month, i + 1))];
  }, [year, month]);

  const thisYear = today.getFullYear();
  const years = useMemo(() => {
    const ys = available.list.map((d) => d.getFullYear());
    const lo = Math.min(thisYear, ...ys);
    const hi = Math.max(thisYear, ...ys);
    const out = [];
    for (let y = hi; y >= lo; y -= 1) out.push(y);
    return out;
  }, [available.list, thisYear]);

  const pick = (d) => {
    onChange?.(toValue(d));
    setOpen(false);
  };

  const shiftMonth = (delta) => setView(new Date(year, month + delta, 1));

  const cellSize = 44;

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center',
          ...(size === 'sm'
            ? { gap: 8, minWidth: 140, height: 38, padding: '0 12px' }
            : { gap: 10, minWidth: 190, height: 50, padding: '0 18px' }),
          background: C.trigger, color: selected ? C.text : C.dim, border: `1px solid ${C.triggerBorder}`,
          borderRadius: 8, fontSize: size === 'sm' ? 15 : 19, fontWeight: size === 'sm' ? 600 : undefined, cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <span style={{ color: C.text, lineHeight: 0 }}><CalendarIcon size={size === 'sm' ? 16 : 20} /></span>
        {selected ? toLabel(selected) : placeholder}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={dialogLabel}
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', ...(align === 'right' ? { right: 0 } : { left: 0 }), zIndex: 1060,
            width: cellSize * 7 + 40, padding: '16px 20px 18px', background: C.panel, color: C.text,
            borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.45)', userSelect: 'none', textAlign: 'center',
          }}
        >
          {/* Header: month ▾ and arrows */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <button
              type="button"
              onClick={() => setMode((m) => (m === 'days' ? 'months' : 'days'))}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', color: C.text, fontSize: 17, fontWeight: 500, cursor: 'pointer', padding: '4px 6px', borderRadius: 6 }}
            >
              {MONTHS_LONG[month]} {year}
              <svg width="12" height="12" viewBox="0 0 12 12" style={{ transform: mode === 'months' ? 'rotate(180deg)' : 'none' }}>
                <polygon points="1,3 11,3 6,9" fill="currentColor" />
              </svg>
            </button>
            {mode === 'days' && (
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" aria-label="Previous month" onClick={() => shiftMonth(-1)} style={navBtn}><Chevron dir="left" /></button>
                <button type="button" aria-label="Next month" onClick={() => shiftMonth(1)} style={navBtn}><Chevron dir="right" /></button>
              </div>
            )}
          </div>

          {mode === 'days' ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(7, ${cellSize}px)`, justifyContent: 'center', marginBottom: 6 }}>
                {WEEKDAYS.map((w, i) => (
                  <div key={i} style={{ height: 32, lineHeight: '32px', fontSize: 15, color: C.text }}>{w}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(7, ${cellSize}px)`, gridAutoRows: cellSize, justifyContent: 'center', rowGap: 4 }}>
                {cells.map((d, i) => {
                  if (!d) return <div key={`b${i}`} />;
                  const key = dayKey(d);
                  const isAvailable = available.days.has(key);
                  const isSelected = selected && dayKey(selected) === key;
                  const isToday = key === todayKey;
                  const isHover = hovered === key && isAvailable && !isSelected;
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={!isAvailable}
                      onClick={() => pick(d)}
                      onMouseEnter={() => setHovered(key)}
                      onMouseLeave={() => setHovered(null)}
                      aria-pressed={!!isSelected}
                      aria-label={`${toLabel(d)}${isAvailable ? ' (available)' : ''}`}
                      style={{
                        width: cellSize - 4, height: cellSize - 4, margin: 2, borderRadius: '50%', padding: 0,
                        fontSize: 16, cursor: isAvailable ? 'pointer' : 'default',
                        fontWeight: isAvailable ? 700 : 400,
                        color: isSelected ? C.selectedText : isAvailable ? C.text : C.dim,
                        background: isSelected ? C.selected : isHover ? C.hover : 'transparent',
                        border: isToday && !isSelected ? `1px solid ${C.todayRing}` : '1px solid transparent',
                      }}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
              {!available.list.length && (
                <div style={{ marginTop: 10, fontSize: 13, color: C.dim }}>{emptyText}</div>
              )}
            </>
          ) : (
            <div style={{ maxHeight: cellSize * 6 + 38, overflowY: 'auto', textAlign: 'left' }}>
              {years.map((y) => (
                <div key={y} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 14, color: C.dim, margin: '4px 6px 6px' }}>{y}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                    {MONTHS.map((m, mi) => {
                      const has = available.months.has(monthKey(y, mi));
                      const current = y === year && mi === month;
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => { setView(new Date(y, mi, 1)); setMode('days'); }}
                          style={{
                            height: 36, borderRadius: 18, fontSize: 14, cursor: 'pointer',
                            border: '1px solid transparent',
                            background: current ? C.selected : 'transparent',
                            color: current ? C.selectedText : has ? C.text : C.dim,
                            fontWeight: has ? 700 : 400,
                          }}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const navBtn = {
  width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'none', color: '#e8e8e8',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
};

export default CalendarPicker;
